import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { EmtrService } from '@ems/emtr';
import { ShellConstant } from '@shell_components/constants/shellConstants';
import { SessionCacheService, SharedConstant, SharedService } from '@ems/shared';
import type { TradeChartData, TradeChartResponse } from '@ems/shared';
import { Observable, Observer, Subscription } from 'rxjs';
import { AuthService } from '@core_services/auth/auth.service';
import { ChartsService } from '@core_services/charts/charts.service';
import { LocalStorageService } from '@core_services/local-storage.service';
import moment from 'moment-timezone';
import { authAgentTableHeader } from '../model/authAgent-model';
import { authAgentTableHeaderSCT } from '../model/authAgent-model';
import { entitlementConst } from '@shell_components/constants/entitlementConstants';
import { Router } from '@angular/router';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Table} from 'primeng/table';
import { ManageUsersService } from '@core_services/manage-users/manage-user.service';
import { ReportsService } from '@core_services/report/reports.service';

/** Shape of the workbench data bundle stored in sessionStorage (ETRE-83545). */
interface WorkbenchCacheBundle {
  clientKey: string;
  addlinkData: any | null;
  addlinkError: boolean;
  restrictionOverrideResponse: any | null;
  tradeAttemptResponse: TradeChartResponse | null;
  tradeAttemptData?: TradeChartData | null;
  masterAccCount: number | null;
  authorizedAgentData: any | null;
}

@Component({
  selector: 'app-workbench',
  templateUrl: './workbench.component.html',
  styleUrls: ['./workbench.component.scss']
})
export class WorkbenchComponent implements OnInit, OnDestroy {

  @ViewChild('authAgentTable') authAgentTable: Table;
  @ViewChild('confirmationMessage', { static: true }) confirmationMessage: any;

  orgId: any;
  shellConstant = ShellConstant;
  entitlementConst = entitlementConst;
  getSharedConstant = SharedConstant;
  private currentOrgSubscription: Subscription;
  private lastClientEmissionKey: string | null = null;
  private tradeAttemptSub: Subscription | null = null;
  private restrictionSub: Subscription | null = null;
  private addlinkSub: Subscription | null = null;
  private tradeAttemptRequestId = 0;
  private restrictionRequestId = 0;
  private addlinkRequestId = 0;

  // ── 15-min sessionStorage cache (ETRE-83545) ──────────────────────────────
  private readonly CACHE_KEY_PREFIX = 'workbench-data';
  private readonly CACHE_VERSION = 'v1';
  private readonly TTL = 15 * 60 * 1000;
  activeCacheKey = this.buildCacheKey('all');
  private pendingResponses = 0;
  private responsesReceived = 0;
  private currentBundle: WorkbenchCacheBundle = { clientKey: '', addlinkData: null, addlinkError: false, restrictionOverrideResponse: null, tradeAttemptResponse: null, masterAccCount: null, authorizedAgentData: null };
  startDateArr: any = [];
  endDateArr: any = [];
  overrideEndDateArr: any = [];
  ugGutTrack = 'adobe-dl_quickuserguide|fireonce';
  dataValues: any = [];

  labels: any;

  /* Restriction Dropdown model variables */
  selectedStartDate: any;
  selectedEndDate: any;

  restrictStartDtValues: any;
  restrictStartDtLabel: any;

  restrictEndDtValues: any;
  restrictEndDtLabel: any;

  overrideEndDtValues: any;
  overrideStartDtLabel: any;
  /* Override Dropdown model variables */
  selectedOverrideStartDate: any;
  selectedOverrideEndDate: any;

  overrideStartDtValues: any;
  overrideEndDtLabel: any;
  restrictionResponse: any;
  overrideResponse: any;
  workbenchData: any = [];
  selectedClientInfo: any;
  authorizedAgentData: any = {
    agentResponse: [],
    agentTimestamp: ''
  };
  authAgentList = [];
  addlinkData: any = {
    addlinkResponse: [],
    addlinkTimestamp: '',
    onHoldCount: 0,
    validationMismatchCount: 0,
    dbsActionNeededCount: 0,
    requireIndexing: 0,
    clientCentralLinks: 0,
    clientCentralDelinks: 0
  };
  authAgentTableColumn: any;
  loader: boolean;
  loadSection: any;
  getUserEntitlement: any;
  showPanel: any;
  showAAPanel = false;
  masterAccCount = 0;
  isWorkbenchComponent = true;
  businessUnit: any;
  userPolicy: any;
  noRUAUEntForDBS = false;
  noRUAUEntForSPS = false;
  showUGForClientAcc = false;
  tradeRulesLinks: any;
  addlinkQuickLinks: any;
  isQuickActionsCollapsed = false;
  toggle = false;
  authorizedAgentForm: UntypedFormGroup;
  isFilterApplied = false;
  isSortingChanged = false;
  noOfRecords: any = 10;
  pageNumResetToFirst = 0;
  accessOnlyForRbs = false;
  accessWithRuleAgentDBS = false;
  accessToDBS = false;
  accessToSPS = false;
  accessWithRuleAgentSPS = false;
  accessWithAuthorizedAgentDBS = false;
  accessWithAuthorizedAgentSPS = false;

  // Trade attempt chart state — wired to API in ETRE-83545
  tradeAttemptData: TradeChartData | null = null;
  tradeAttemptLoading = false;
  tradeAttemptError = false;
  addlinkLoading = false;
  addlinkError = false;
  donutLoading = false;
  donutError = false;
  tradeAttemptTabLabels: string[] = [];
  authorizedAgentError = false;

  constructor(private router: Router, public manageUsersService: ManageUsersService, public e: EmtrService, private authService: AuthService, public fb: UntypedFormBuilder,
              private chartsService: ChartsService, public localStrService: LocalStorageService, public sharedService: SharedService,
              private sessionCache: SessionCacheService, private reportsService: ReportsService) {
  }

  ngOnInit() {
    this.isQuickActionsCollapsed = localStorage.getItem('workbenchQuickActionsCollapsed') === 'true';
    this.startDateArr = [
      {label: '≤3', value: 3},
      {label: '≤7', value: 7}
    ];
    this.selectedStartDate = this.selectedOverrideStartDate = this.startDateArr[0];
    this.authorizedAgentForm = this.fb.group({
      authAgentFirstNm: [''],
      authAgentLastNm: ['']
    });
    this.endDateArr = this.overrideEndDateArr = Array.from(this.startDateArr);
   this.selectedEndDate = this.selectedOverrideEndDate = this.endDateArr[0];
    this.userPolicy = this.sharedService.getPolicyResponse('success');
    this.currentOrgSubscription = this.e.clientInformation.subscribe(clientInfo => {
      this.sharedService.clearErrorMessage();
      if (clientInfo !== null) {
        const clientEmissionKey = this.buildClientEmissionKey(clientInfo);
        // Ignore repeated identical emissions to avoid cancel-then-refetch churn.
        if (clientEmissionKey === this.lastClientEmissionKey) {
          return;
        }
        this.lastClientEmissionKey = clientEmissionKey;

        this.businessUnit = clientInfo.businessUnit ? clientInfo.businessUnit : 'All';
        /* eslint-disable-next-line max-len */
        this.accessOnlyForRbs = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.length === 1 && this.userPolicy.data.userPolicy.buPolicy.map(x => x.businessUnit).indexOf('RBS') !== -1) || (this.userPolicy.data.userPolicy.buPolicy === null && this.userPolicy.data.clients && this.userPolicy.data.clients.every(x => x.businessUnit === 'RBS'));
        /* to display workbench RBS version without RAA access is der for DBS/SPS BU */
        /* eslint-disable-next-line max-len */
        this.accessToDBS = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'DBS')) || (this.userPolicy.data.userPolicy.buPolicy === null && this.userPolicy.data.clients && this.userPolicy.data.clients.some(x => x.businessUnit === 'DBS'));
        /* eslint-disable-next-line max-len */
        this.accessToSPS = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'SPS')) || (this.userPolicy.data.userPolicy.buPolicy === null && this.userPolicy.data.clients && this.userPolicy.data.clients.some(x => x.businessUnit === 'SPS'));
        /* eslint-disable-next-line max-len */
        this.accessWithRuleAgentDBS = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'DBS' && val.entitlementList.map(y => y.entitlementName).indexOf('RULES_SUMMARY') !== -1)) || (this.userPolicy.data.userPolicy.buPolicy === null && (this.userPolicy.data.userPolicy.clientPolicy.some(x => this.userPolicy.data.clients.some(y => x.clientId === y.clientId && y.businessUnit === 'DBS' && x.entitlementList.map(z => z.entitlementName).indexOf('RULES_SUMMARY') !== -1))));
        /* eslint-disable-next-line max-len */
        this.accessWithRuleAgentSPS = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'SPS' && val.entitlementList.map(y => y.entitlementName).indexOf('RULES_SUMMARY') !== -1)) || (this.userPolicy.data.userPolicy.buPolicy === null && (this.userPolicy.data.userPolicy.clientPolicy.some(x => this.userPolicy.data.clients.some(y => x.clientId === y.clientId && y.businessUnit === 'SPS' && x.entitlementList.map(z => z.entitlementName).indexOf('RULES_SUMMARY') !== -1))));
        /* eslint-disable-next-line max-len */
        this.accessWithAuthorizedAgentDBS = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'DBS' && val.entitlementList.map(y => y.entitlementName).indexOf('ACCOUNT_SUMMARY') !== -1)) || (this.userPolicy.data.userPolicy.buPolicy === null && (this.userPolicy.data.userPolicy.clientPolicy.some(x => this.userPolicy.data.clients.some(y => x.clientId === y.clientId && y.businessUnit === 'DBS' && x.entitlementList.map(z => z.entitlementName).indexOf('ACCOUNT_SUMMARY') !== -1))));
        /* eslint-disable-next-line max-len */
        this.accessWithAuthorizedAgentSPS = (this.userPolicy.data.userPolicy.buPolicy && this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === 'SPS' && val.entitlementList.map(y => y.entitlementName).indexOf('ACCOUNT_SUMMARY') !== -1)) || (this.userPolicy.data.userPolicy.buPolicy === null && (this.userPolicy.data.userPolicy.clientPolicy.some(x => this.userPolicy.data.clients.some(y => x.clientId === y.clientId && y.businessUnit === 'SPS' && x.entitlementList.map(z => z.entitlementName).indexOf('ACCOUNT_SUMMARY') !== -1))));
        /* eslint-disable-next-line max-len */
        if (this.businessUnit === 'RBS' || this.accessOnlyForRbs || (this.accessToDBS && this.accessToSPS && !this.accessWithRuleAgentDBS && !this.accessWithRuleAgentSPS) || (!this.accessToDBS && !this.accessToSPS) || (this.accessToDBS && !this.accessToSPS && !this.accessWithRuleAgentDBS) || (!this.accessToDBS && this.accessToSPS && !this.accessWithRuleAgentSPS)) {
          this.tradeRulesLinks = this.shellConstant.workBenchChart.rules.RBSRulesLinks;
        } else {
          this.tradeRulesLinks = this.shellConstant.workBenchChart.rules.RulesLinks;
        }

        /* eslint-disable-next-line max-len */
        if (this.businessUnit === 'RBS' || this.accessOnlyForRbs || (this.accessToDBS && this.accessToSPS && !this.accessWithAuthorizedAgentDBS && !this.accessWithAuthorizedAgentSPS) || (!this.accessToDBS && !this.accessToSPS) || (this.accessToDBS && !this.accessToSPS && !this.accessWithAuthorizedAgentDBS) || (!this.accessToDBS && this.accessToSPS && !this.accessWithAuthorizedAgentSPS) || this.sharedService.showHistoricalView.value) {
          this.addlinkQuickLinks = this.getSharedConstant['RBS'].addLinkQuickLink;
        } else {
          this.addlinkQuickLinks = this.getSharedConstant['DBS'].addLinkQuickLink;
        }

        this.orgId = clientInfo.orgId;
        this.chartsService.orgId = this.orgId;
        const userPolicy = this.authService.getUserPolicy();
        const clientPolicy = this.sharedService.policy.data.userPolicy;
        if (this.orgId) {
          this.getUserEntitlement = clientPolicy.buPolicy && clientPolicy.buPolicy.length > 0 ? clientPolicy.buPolicy : clientPolicy.clientPolicy;
        } else {
          this.getUserEntitlement = this.sharedService.userPolicyEntitlementList;
        }
        this.authorizedAgentData.agentResponse = this.authAgentList = [];
        this.selectedClientInfo = JSON.parse(this.localStrService.getProfile());
        this.showPanel = this.sharedService.checkUserEntitlements(this.getUserEntitlement);
        const clientCacheKey = this.orgId ? String(this.orgId) : 'all';
        this.activeCacheKey = this.buildCacheKey(clientCacheKey);

        /* If client is selected and the selected client does not have access to Rule Agent and Auth Agent access, then navigate to Admin menu */
        if (this.isWorkbenchComponent && this.orgId && !this.sharedService.checkEntitlements(entitlementConst.navUUID.emtrAccess.ruleSummary.uuid, entitlementConst.navUUID.accountSummary.uuid, '')) {
          this.sharedService.loadDataTrackValue('client_redirect');
          this.isWorkbenchComponent = false;
          if (this.sharedService.checkEntitlements(entitlementConst.navUUID.emtrAccess.instrumentsSummary.uuid, '', '')) {
            this.router.navigate(['/ems/emtr/instrument-groups/instrument-group-list']);
          } else {
            this.router.navigate(['ems/admin/manage-clients']);
          }
        }

        // ── 15-min sessionStorage cache check (ETRE-83545) ──────────────────
        const cachedEntry = this.sessionCache.get<WorkbenchCacheBundle>(this.activeCacheKey);
        const isCacheValid = cachedEntry
          && cachedEntry.value.clientKey === clientCacheKey
          && !this.sessionCache.isStale(this.activeCacheKey, this.TTL);

        if (isCacheValid) {
          this.cancelInFlightRequests();
          this.applyData(cachedEntry.value);
          // masterAccCount and authorizedAgentData are restored (or refetched) inside applyData
        } else {
          this.cancelInFlightRequests();
          // Evict all previous-client entries so only the current client is cached
          this.sessionCache.clearByPrefix(this.CACHE_KEY_PREFIX);
          this.pendingResponses =
            (this.showPanel.hasAddlink ? 1 : 0) +
            (this.showPanel.hasEmtr ? 1 : 0) +
            (this.showPanel.hasEmtr && this.orgId ? 1 : 0);
          this.responsesReceived = 0;
          this.currentBundle = { clientKey: clientCacheKey, addlinkData: null, addlinkError: false, restrictionOverrideResponse: null, tradeAttemptResponse: null, masterAccCount: null, authorizedAgentData: null };

          if (this.showPanel.hasAddlink) {
            if (!this.orgId) {
              this.selectedClientInfo = { clientId: 0 };
              this.fetchAddlinkData('');
            } else {
              this.fetchAddlinkData('addLink');
              this.fetchMasterAccCount(this.orgId);
              this.fetchAuthorizedAgents('clients');
            }
          }

          if (this.showPanel.hasEmtr) {
            this.fetchRestrictionResponse();
            if (this.orgId) {
              this.fetchTradeAttemptData();
            } else {
              this.tradeAttemptData = null;
            }
          }
        }

        /* to display user guide to users with client leve access */
        if (this.userPolicy.data.userPolicy.clientPolicy && this.userPolicy.data.userPolicy.clientPolicy.length > 0) {
          /* eslint-disable-next-line max-len */
          const showUGForClients = this.userPolicy.data.userPolicy.clientPolicy.some(x => this.userPolicy.data.clients[x.clientId] !== this.getSharedConstant.rbsBUText && (x.entitlementList.map(y => y.entitlementName).indexOf('ACCOUNT_SUMMARY') !== -1 || (x.entitlementList.map(y => y.entitlementName).indexOf('RULES_SUMMARY') !== -1)));

          if (this.businessUnit === 'All') {
            this.showUGForClientAcc = showUGForClients;
          } else if (this.businessUnit !== this.getSharedConstant.rbsBUText) {
            /* eslint-disable-next-line max-len */
            this.showUGForClientAcc = this.userPolicy.data.userPolicy.clientPolicy.some(x => x.clientId === clientInfo.clientId && (x.entitlementList.map(y => y.entitlementName).indexOf('ACCOUNT_SUMMARY') !== -1 || (x.entitlementList.map(y => y.entitlementName).indexOf('RULES_SUMMARY') !== -1)));
          } else {
            this.showUGForClientAcc = false;
          }
        }
      }

      /* To rest the form, toggle and the table property - to reste the sort field and pagination */
      this.restAAToggleAndTable();
    });

    if (this.userPolicy.data.userPolicy.buPolicy) {
      /* to display user guide when RA and AA access is der for DBS/SPS BU */
      /* eslint-disable-next-line max-len */
      this.noRUAUEntForDBS = (this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === this.shellConstant.manageUserPage.dbs && (val.entitlementList.map(y => y.entitlementName).indexOf('ACCOUNT_SUMMARY') !== -1 || (val.entitlementList.map(y => y.entitlementName).indexOf('RULES_SUMMARY') !== -1))));
      /* eslint-disable-next-line max-len */
      this.noRUAUEntForSPS = (this.userPolicy.data.userPolicy.buPolicy.some(val => val.businessUnit === this.shellConstant.manageUserPage.sps && (val.entitlementList.map(y => y.entitlementName).indexOf('ACCOUNT_SUMMARY') !== -1 || (val.entitlementList.map(y => y.entitlementName).indexOf('RULES_SUMMARY') !== -1))));
    }
    if (localStorage.getItem('inactiveClientSelection') === 'true') {
      this.sharedService.showHistoricalView.next(true);
      this.sharedService.showHistoricalView.asObservable();
    }

    setTimeout(() => {
      // Call the confirmation popup when user is Authorized Agent for DBS BU and
        // uses does not acknowledge the credential sharing warning
      const credentialSharingAcknowledged = this.userPolicy.data.credentialSharingWarningInfo?.acknowledged;
      if (this.accessWithAuthorizedAgentDBS && !credentialSharingAcknowledged && !this.sharedService.isInternalApp() && (this.businessUnit === 'DBS' || this.businessUnit === 'All')) {
        this.confirmationPopup();
      }
    }, 500);
  }

  acceptCredentialSharingWarning() {
    this.manageUsersService.credShareWarning(true).subscribe({
      error: (err) => console.error('Failed to save credential sharing preference', err)
    });
  }

  
  confirmationPopup() {
    const hideWarning = localStorage.getItem('hideCredentialSharingWarning');
    if (!hideWarning) {
      const confirmObj = {
        header: this.getSharedConstant.dbsAuthAgentMsg.dbsAuthAgentHeader,
        message: this.getSharedConstant.dbsAuthAgentMsg.dbsAuthAgentwarningMsg,
        acceptBtn: this.getSharedConstant.dbsAuthAgentMsg.dismissBtn,
        rejectBtn: this.getSharedConstant.confirmationDialog.hideRejectButton,
        closeIconButton: false,
        showDoNotShowAgain: true
      };
      new Observable((observer: Observer<any>) => {
        this.confirmationMessage.showConfirmMsg(confirmObj, observer);
      }).subscribe(result => {
        if (result && result.accepted) {
          // Hide for this browser session (survives page refresh)
          localStorage.setItem('hideCredentialSharingWarning', 'true');
          if (result.dontshow) {
            this.acceptCredentialSharingWarning();
          }
        }
      });
    }
  }
  ngOnDestroy(): void {
    this.isWorkbenchComponent = false;
    this.currentOrgSubscription.unsubscribe();
    this.cancelInFlightRequests();
    this.sharedService.setRefresh.emit(false);
  }

  private buildCacheKey(orgId: string): string {
    return `${this.CACHE_KEY_PREFIX}:${this.CACHE_VERSION}:${orgId}`;
  }

  private buildClientEmissionKey(clientInfo: any): string {
    const orgId = clientInfo?.orgId ?? '';
    const businessUnit = clientInfo?.businessUnit ?? 'All';
    const clientId = clientInfo?.clientId ?? 'none';
    return `${orgId}:${businessUnit}:${clientId}`;
  }

  private cancelInFlightRequests(): void {
    this.tradeAttemptSub?.unsubscribe();
    this.restrictionSub?.unsubscribe();
    this.addlinkSub?.unsubscribe();
  }

  // ── 15-min cache helpers (ETRE-83545) ────────────────────────────────────

  /**
   * Write the current bundle to sessionStorage after every API response so that a
   * quick navigation away does not lose data already arrived.  Writing after each
   * response is safe because we always overwrite the whole bundle (stale sections
   * remain null and are refetched in applyData on the next visit).
   * The `pendingResponses === 0` guard skips the batch-completion counter increment
   * for callers (e.g. manual refresh) that do not participate in a pending batch;
   * the cache write itself always executes so those paths also stay in sync.
   */
  private tryCacheWrite(): void {
    this.sessionCache.set(this.activeCacheKey, this.currentBundle);
    if (this.pendingResponses === 0) { return; }
    this.responsesReceived++;
    if (this.responsesReceived >= this.pendingResponses) {
      this.pendingResponses = 0;
      this.responsesReceived = 0;
    }
  }

  /**
   * Restore all cached workbench data to component state instantly.
   * Any section that was null in the bundle (not yet loaded when the user navigated
   * away, or API-errored) is re-fetched so the UI is always fully populated.
   */
  private applyData(bundle: WorkbenchCacheBundle): void {
    this.loader = false;
    this.addlinkLoading = false;
    this.donutLoading = false;
    const clientCacheKey = this.orgId ? String(this.orgId) : 'all';

    // Determine which sections need a re-fetch before touching component state
    const refetchAddlink     = !bundle.addlinkData && this.showPanel?.hasAddlink;
    const refetchRestriction = !bundle.restrictionOverrideResponse && this.showPanel?.hasEmtr;
    const tradeAttemptSource = bundle.tradeAttemptResponse ?? bundle.tradeAttemptData ?? null;
    const refetchTrade       = !tradeAttemptSource && this.showPanel?.hasEmtr && this.orgId;
    const refetchMaster      = bundle.masterAccCount === null && this.showPanel?.hasAddlink && !!this.orgId;
    const refetchAgent       = bundle.authorizedAgentData === null && this.showPanel?.hasAddlink && !!this.orgId;

    if (bundle.addlinkData) {
      this.addlinkData = bundle.addlinkData;
    }
    this.addlinkError = !!(bundle.addlinkError);
    if (bundle.restrictionOverrideResponse) {
      const res = bundle.restrictionOverrideResponse;
      this.selectedStartDate = this.selectedOverrideStartDate = this.startDateArr[0];
      this.selectedEndDate = this.selectedOverrideEndDate = this.endDateArr[0];
      this.restrictionResponse = res.data?.scheduledActiveRestrictions;
      if (this.restrictionResponse) { this.loadChartForDates(this.restrictionResponse, 'restriction'); }
      this.overrideResponse = res.data?.scheduledActiveOverrides;
      if (this.overrideResponse) { this.loadChartForDates(this.overrideResponse, 'override'); }
    }
    this.donutError = !!(this.showPanel?.hasEmtr && !bundle.restrictionOverrideResponse);
    this.tradeAttemptData = this.getTradeChartDataFromCache(tradeAttemptSource);
    this.tradeAttemptTabLabels = this.sharedService.buildTabsFromPayload(this.tradeAttemptData);
    this.tradeAttemptLoading = !!refetchTrade;
    this.tradeAttemptError = false;

    // Restore masterAccCount from cache
    if (bundle.masterAccCount !== null) {
      this.masterAccCount = bundle.masterAccCount;
    }
    // Restore authorized-agent panel from cache
    if (bundle.authorizedAgentData !== null) {
      this.authorizedAgentData = bundle.authorizedAgentData.agentData;
      this.authAgentList       = bundle.authorizedAgentData.agentList;
      this.authAgentTableColumn = bundle.authorizedAgentData.tableColumn;
      this.showAAPanel = true;
      this.authorizedAgentError = false;
    }

    // Re-fetch any section whose data was absent in the cached bundle.
    const refetchCount = [refetchAddlink, refetchRestriction, refetchTrade].filter(Boolean).length;
    if (refetchCount > 0 || refetchMaster || refetchAgent) {
      // Seed the bundle with whatever was already cached so tryCacheWrite merges properly.
      this.currentBundle = {
        clientKey: clientCacheKey,
        addlinkData: bundle.addlinkData,
        addlinkError: bundle.addlinkError ?? false,
        restrictionOverrideResponse: bundle.restrictionOverrideResponse,
        tradeAttemptResponse: bundle.tradeAttemptResponse ?? null,
        tradeAttemptData: bundle.tradeAttemptData ?? null,
        masterAccCount: bundle.masterAccCount,
        authorizedAgentData: bundle.authorizedAgentData
      };
      this.pendingResponses = refetchCount; // only the main 3 participate in the batch counter
      this.responsesReceived = 0;
      if (refetchAddlink)     { this.fetchAddlinkData(this.orgId ? 'addLink' : ''); }
      if (refetchRestriction) { this.fetchRestrictionResponse(); }
      if (refetchTrade)       { this.fetchTradeAttemptData(); }
      if (refetchMaster)      { this.fetchMasterAccCount(this.orgId); }
      if (refetchAgent)       { this.fetchAuthorizedAgents('clients'); }
    }
  }

  /**
   * Re-fetch all three cacheable API sections (addlink count, restriction/
   * override donut, trade attempt).  Called when the tab returns to visibility
   * after the 15-min TTL has elapsed.
   */
  private refreshWorkbenchData(): void {
    if (!this.showPanel) { return; }
    this.cancelInFlightRequests();
    // Evict all entries so the refreshed data is the only thing cached
    this.sessionCache.clearByPrefix(this.CACHE_KEY_PREFIX);
    const clientKey = this.orgId ? String(this.orgId) : 'all';
    this.pendingResponses =
      (this.showPanel.hasAddlink ? 1 : 0) +
      (this.showPanel.hasEmtr ? 1 : 0) +
      (this.showPanel.hasEmtr && this.orgId ? 1 : 0);
    this.responsesReceived = 0;
    this.currentBundle = { clientKey, addlinkData: null, addlinkError: false, restrictionOverrideResponse: null, tradeAttemptResponse: null, masterAccCount: null, authorizedAgentData: null };
    if (this.showPanel.hasAddlink) {
      this.fetchAddlinkData(this.orgId ? 'addLink' : '');
      if (this.orgId) {
        this.fetchMasterAccCount(this.orgId);
        this.fetchAuthorizedAgents('clients');
      }
    }
    if (this.showPanel.hasEmtr) {
      this.fetchRestrictionResponse();
      if (this.orgId) {
        this.fetchTradeAttemptData();
      }
    }
  }

  /**
   * When the user returns to this tab after the cache TTL has expired, discard
   * the stale entry and re-fetch so the data is always within 15 minutes.
   */
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible'
        && this.showPanel
        && !this.isSessionNearExpiry()
        && this.sessionCache.isStale(this.activeCacheKey, this.TTL)) {
      this.refreshWorkbenchData();
    }
  }

  /* Skip the tab-focus refetch once the token is inside its refresh window; otherwise the
     refetch renews the token and pushes the timeout popup past tknExpireTime. */
  private isSessionNearExpiry(): boolean {
    const expiry = this.localStrService.getExpiryDateTime();
    if (!expiry) {
      return false;
    }
    const msLeft = moment(expiry, this.shellConstant.sessionConstants.calendarFormat).diff(moment());
    return msLeft <= this.shellConstant.sessionConstants.externalUserTimeoutInminutes * 60 * 1000;
  }

  reduceArr(Arr) {
    return Arr.reduce((acc, curr) => {
      return acc + curr;
    }, 0);
  }

  pushValues(value, label, type) {
    const valueLessThanTwo: any = [];
    const valueLessThanFive: any = [];
    const wholeArr: any = [];
    if (value) {
      value.map((val, i) => {
        if (val.id <= 0) {
          this.dataValues.push(val.value);
        } else if ((val.id > 0) && (val.id <= 2)) {
          valueLessThanTwo.push(val.value);
        } else if ((val.id > 2) && (val.id <= 5)) {
          valueLessThanFive.push(val.value);
        } else {
          wholeArr.push(val.value);
        }
      });
      this.dataValues.push(this.reduceArr(valueLessThanTwo));
      this.dataValues.push(this.reduceArr(valueLessThanFive));
      this.dataValues.push(this.reduceArr(wholeArr));
      this.labels = this.shellConstant.workBenchChart.weekLabels;
      this.generateChart(label, type);
    }

  }

  fetchTradeAttemptData() {
    const requestId = ++this.tradeAttemptRequestId;
    this.tradeAttemptSub?.unsubscribe();
    this.tradeAttemptLoading = true;
    this.tradeAttemptError = false;
    this.tradeAttemptSub = this.chartsService.getTradeAttemptCount().subscribe({
      next: (res) => {
        if (requestId !== this.tradeAttemptRequestId) { return; }
        if (!this.isValidTradeAttemptResponse(res)) {
          this.tradeAttemptLoading = false;
          this.tradeAttemptError = false;
          this.tradeAttemptData = res;
          this.tradeAttemptTabLabels = [];
          this.finalizeTradeAttemptResponse();
          this.sharedService.clearErrorMessage();
          return;
        }
        this.handleTradeAttemptSuccess(res);
      },
      error: () => {
        this.handleTradeAttemptError(requestId);
      }
    });
  }

  onTradeAttemptDownloadReport(event: { fromDate: string; toDate: string }): void {
    this.reportsService.setReportName(this.shellConstant.reports.TradeAttempts.name);
    this.reportsService.pendingTradeFromDate = event.fromDate;
    this.reportsService.pendingTradeToDate = event.toDate;
    this.router.navigate(['/ems/reports/preset-reports/trade-attempts']);
  }

  fetchRestrictionResponse() {
    const requestId = ++this.restrictionRequestId;
    this.restrictionSub?.unsubscribe();
    this.donutLoading = true;
    this.donutError = false;
    /* Reset dropdowns to default (≤3) on every client switch */
    this.selectedStartDate = this.selectedOverrideStartDate = this.startDateArr[0];
    this.selectedEndDate = this.selectedOverrideEndDate = this.endDateArr[0];
    /* Load Restriction Doughnut chart */
    this.restrictionSub = this.chartsService.getRestrictOrOverrideData().subscribe({
      next: (res) => {
        if (requestId !== this.restrictionRequestId) { return; }
        this.donutLoading = false;
        this.donutError = false;
        this.restrictionResponse = res.data.scheduledActiveRestrictions;
        if (this.restrictionResponse) {
          this.loadChartForDates(this.restrictionResponse, 'restriction');
        }
        this.overrideResponse = res.data.scheduledActiveOverrides;
        if (this.overrideResponse) {
          this.loadChartForDates(this.overrideResponse, 'override');
        }
        this.currentBundle.restrictionOverrideResponse = res;
        this.tryCacheWrite();
        this.sharedService.clearErrorMessage();
      },
      error: () => {
        if (requestId !== this.restrictionRequestId) { return; }
        this.donutLoading = false;
        this.donutError = true;
        this.restrictionResponse = null;
        this.overrideResponse = null;
        this.restrictStartDtValues = null;
        this.restrictEndDtValues = null;
        this.overrideStartDtValues = null;
        this.overrideEndDtValues = null;
        this.currentBundle.restrictionOverrideResponse = null;
        this.tryCacheWrite();
        this.sharedService.clearErrorMessage();
      }
    });
  }

  fetchAuthorizedAgents(type) {
    /* If Refresh Icon clicked Functionality handled */
    if (type === 'authorizedAgents') {
      this.sharedService.setRefresh.emit(true);
      this.loadSection = type;
      this.loader = true;
    } else {
      /* Initial Load/Client Selection Load */
      this.sharedService.setRefresh.emit(false);
    }

    if (this.selectedClientInfo && (this.selectedClientInfo.businessUnit === 'DBS') && this.showPanel.hasAddlink) {
      this.chartsService.getAuthorizedAgent(this.selectedClientInfo.orgId).subscribe({
        next: res => {
          this.authAgentTableColumn = res.data.dbsauthAgentlist !== null ? authAgentTableHeader : authAgentTableHeaderSCT;
          this.loader = false;
          this.authorizedAgentData = {
            agentResponse: res.data.dbsauthAgentlist !== null ? res.data.dbsauthAgentlist : res.data.authAgentlist,
            agentTimestamp: moment(this.authorizedAgentData.timestamp).tz('America/New_York').format('hh:mm A')
          };
          this.authAgentList = this.authorizedAgentData.agentResponse;
          this.authorizedAgentError = false;
          this.showAAPanel = true;
          // Cache only on initial/client-change load; manual refresh ('authorizedAgents') always fetches fresh
          if (type !== 'authorizedAgents') {
            this.currentBundle.authorizedAgentData = {
              agentData: this.authorizedAgentData,
              agentList: this.authAgentList,
              tableColumn: this.authAgentTableColumn
            };
            this.tryCacheWrite();
          }
          this.sharedService.clearErrorMessage();
        },
        error: () => {
          this.loader = false;
          this.showAAPanel = true;
          this.authorizedAgentError = true;
          this.sharedService.clearErrorMessage();
        }
      });
    } else {
      this.showAAPanel = false;
    }
  }

  fetchAddlinkData(type) {
    if (!this.showPanel?.hasAddlink) {
      this.addlinkLoading = false;
      this.addlinkError = false;
      return;
    }

    const isAddlinkRefresh = type === 'addLink';
    this.sharedService.setRefresh.emit(isAddlinkRefresh);
    if (isAddlinkRefresh) {
      this.loadSection = type;
      this.loader = true;
    }

    this.addlinkLoading = true;
    this.addlinkError = false;
    const requestId = ++this.addlinkRequestId;
    this.addlinkSub?.unsubscribe();

    const addlinkRequest$ = this.selectedClientInfo?.clientId
      ? this.chartsService.getAddlinkClientData(this.selectedClientInfo.orgId)
      : this.chartsService.getAddlinkDataAll();

    this.addlinkSub = addlinkRequest$.subscribe({
      next: (res) => {
        if (requestId !== this.addlinkRequestId) { return; }
        this.updateAddlinkRequestStatus(res);
        this.currentBundle.addlinkData = this.addlinkData;
        this.currentBundle.addlinkError = false;
        this.tryCacheWrite();
        this.sharedService.clearErrorMessage();
      },
      error: () => {
        if (requestId !== this.addlinkRequestId) { return; }
        this.loader = false;
        this.addlinkLoading = false;
        this.addlinkError = true;
        this.currentBundle.addlinkData = null;
        this.currentBundle.addlinkError = true;
        this.tryCacheWrite();
        this.sharedService.clearErrorMessage();
      }
    });

  }

  /* updating status count for addlink*/
  updateAddlinkRequestStatus(res) {
    this.loader = false;
    this.addlinkLoading = false;
    this.addlinkError = false;
    const response = res.data;
    this.addlinkData = {
      onHoldCount: response.onHoldCount,
      validationMismatchCount: response.validationMismatchCount,
      dbsActionNeededCount: response.dbsActionNeededCount,
      requireIndexing: response.indexMissingCount,
      clientCentralLinks: response.clientCentralLinkCount,
      clientCentralDelinks: response.clientCentralDelinkCount,
      loaErrorCount: response.loaError,
      loaPendingCount: response.loaPendingCount,
      loaInReviewCount: response.loaInReviewCount,
      addlinkTimestamp: moment.tz('America/New_York').format('hh:mm A')
    };
  }

  /* Loading both startDate & endDate Doughnut chart for Restriction/Override*/
  loadChartForDates(res, type) {
    const initialLoadLabel = ['startDate', 'endDate'];
    initialLoadLabel.forEach((element) => {
      this.loadSelectedChart(res[element], 3, element, type);
    });
  }

  getDropdownVal(days, label, type) {
    const updatedResponse = (type === 'restriction') ? this.restrictionResponse : this.overrideResponse;
    this.loadSelectedChart(updatedResponse[label], days.value, label, type);
  }

  loadSelectedChart(res, days, label, type) {
    this.dataValues = [];
    if (res && days === 3) {
      res.map((val, i) => {
        if (i <= days) {
          this.dataValues.push(val.value);
        }
      });
      this.labels = this.shellConstant.workBenchChart.dayLabels;
      this.generateChart(label, type);
    } else {
      this.pushValues(res, label, type);
    }
  }

  validateResponse(data) {
    return data && data.every(item => item === 0);
  }

  generateChart(label, type) {
    this.dataValues.reverse();
    switch (label) {
      case 'startDate': {
        (type === 'restriction') ? this.restrictStartDtValues = this.dataValues : this.overrideStartDtValues = this.dataValues;
        (type === 'restriction') ? this.restrictStartDtLabel = this.labels : this.overrideStartDtLabel = this.labels;
        break;
      }

      case 'endDate': {
        (type === 'restriction') ? this.restrictEndDtValues = this.dataValues : this.overrideEndDtValues = this.dataValues;
        (type === 'restriction') ? this.restrictEndDtLabel = this.labels : this.overrideEndDtLabel = this.labels;
        break;
      }
    }
  }

  fetchMasterAccCount(id) {
    if (id !== 0 && this.businessUnit !== this.getSharedConstant.rbsBUText) {
      this.chartsService.getMasterAccount(id).subscribe({
        next: (res) => {
          this.masterAccCount = res.data ? res.data.length : 0;
          this.currentBundle.masterAccCount = this.masterAccCount;
          this.tryCacheWrite();
        },
        error: () => {
          this.masterAccCount = 0;
          // Leave currentBundle.masterAccCount as null so it is re-fetched next visit
        }
      });
    }
  }

  redirectToRequestList(status) {
    /* Set isDTValueUpdated to true, to call the loadpage on navigation to request-list page. */
    this.sharedService.isDTValueUpdated = true;
    let gutTrackValue = 'linkid-lc_rl_';
    if (status === this.shellConstant.workBenchChart.addLink.requestStatus.clientCentralLinks) {
      gutTrackValue = gutTrackValue + 'cc_addlink';
    } else if (status === this.shellConstant.workBenchChart.addLink.requestStatus.clientCentralDelinks) {
      gutTrackValue = gutTrackValue + 'cc_delink';
    } else {
      gutTrackValue = gutTrackValue + status.split(' ').join('');
    }
    this.sharedService.gutTracking(gutTrackValue.trim().toLowerCase());
    this.sharedService.requestStatus = status;
    this.router.navigate(['ems/addLink']);
  }

  quickLinkNavigation(link, dataTrack) {
    /* Set isDTValueUpdated value to true, which will let the call of pageLoad method on navigating to respective screen based on the quick link clicked. */
    this.sharedService.isDTValueUpdated = true;
    this.sharedService.gutTracking(dataTrack);
    this.router.navigate([link]);
  }

  toggleQuickActionsSection() {
    this.isQuickActionsCollapsed = !this.isQuickActionsCollapsed;
    localStorage.setItem('workbenchQuickActionsCollapsed', String(this.isQuickActionsCollapsed));
  }

  toggleFunction() {
    /* Filter icon click GUTtracking value for adobe analytics */
    this.sharedService.gutTracking('adobe-fl_authorizedagentlistfilter|fireonce');
    this.toggle = !this.toggle;
  }

  applyFilter() {
    /* Reload authAgentList value everytime with original list to have correct value filtered from the original list */
    this.authAgentList = this.authorizedAgentData.agentResponse;
    this.isFilterApplied = true;
    /* Set the page back to 1st page with the help of first attribute of p-table whenever apply button is clicked */
    this.pageNumResetToFirst = 0;
    const fN = this.authorizedAgentForm.get('authAgentFirstNm').value ? this.authorizedAgentForm.get('authAgentFirstNm').value.trim() : '';
    const lN = this.authorizedAgentForm.get('authAgentLastNm').value ? this.authorizedAgentForm.get('authAgentLastNm').value.trim() : '';
    /* covert both the comparing value to either lowe or upper case to have the accurate results, as includes ic casesensitive  */
    this.authAgentList = this.authAgentList.filter(agent => agent.authAgentFirstNm.toLowerCase().includes(fN.toLowerCase()) && agent.authAgentLastNm.toLowerCase().includes(lN.toLowerCase()));
  }

  resetFilterAndSortApplied() {
    this.isSortingChanged = false;
    this.isFilterApplied = false;
    this.authorizedAgentForm.reset();
    /* Reload authAgentList value with original list when clear button is clicked */
    this.authAgentList = [...this.authorizedAgentData.agentResponse];
    /* Set the sortField back to authAgenLastNm */
    if (this.authAgentTable) {
      this.authAgentTable._sortField = 'authAgentLastNm';
    }
  }

  restAAToggleAndTable() {
    if (this.authAgentTable) {
      this.authAgentTable.reset();
    }
    this.resetFilterAndSortApplied();
    this.toggle = false;
  }
  
  private handleTradeAttemptSuccess(result: { [key: string]: any }): void {
    const transformedResult = this.transformTradeAttemptResponse(result as TradeChartResponse);
    this.tradeAttemptData = transformedResult;
    this.tradeAttemptTabLabels = this.sharedService.buildTabsFromPayload(transformedResult);
    this.tradeAttemptLoading = false;
    this.finalizeTradeAttemptResponse(result as TradeChartResponse);
    this.sharedService.clearErrorMessage();
  }
  private transformTradeAttemptResponse(result: TradeChartResponse): TradeChartData {
    const { tradeAttemptsInfoList, ...rest } = result;
    const byPeriod = (tradeAttemptsInfoList ?? [])
      .filter((item: any) => this.sharedService.parsePeriodInDays(item?.periodInDays) !== null)
      .reduce((acc: TradeChartData, item: any) => {
        acc[String(item.periodInDays)] = item;
        return acc;
      }, {} as TradeChartData);
    return { ...rest, ...byPeriod };
  }

  private handleTradeAttemptError(requestId: number): void {
    if (requestId !== this.tradeAttemptRequestId) { return; }
    this.sharedService.clearErrorMessage();
    this.tradeAttemptLoading = false;
    this.tradeAttemptError = true;
    this.tradeAttemptData = null;
    this.tradeAttemptTabLabels = [];
    this.finalizeTradeAttemptResponse();
  }

  private finalizeTradeAttemptResponse(rawResponse?: TradeChartResponse): void {
    this.currentBundle.tradeAttemptResponse = rawResponse ?? null;
    this.currentBundle.tradeAttemptData = undefined;
    this.tryCacheWrite();
  }

  private isValidTradeAttemptResponse(result: any): result is TradeChartResponse {
    if (!result || !Array.isArray(result.tradeAttemptsInfoList) || result.tradeAttemptsInfoList.length === 0) {
      return false;
    }
    if (!Array.isArray(result.tradeAttemptsPeriodicCountInfo)) {
      return false;
    }
    return result.tradeAttemptsInfoList.some((item: any) => this.sharedService.parsePeriodInDays(item?.periodInDays) !== null);
  }

  private getTradeChartDataFromCache(data: TradeChartResponse | TradeChartData | null): TradeChartData | null {
    if (!data) {
      return null;
    }
    if (Array.isArray((data as TradeChartResponse).tradeAttemptsInfoList)) {
      return this.transformTradeAttemptResponse(data as TradeChartResponse);
    }
    return data as TradeChartData;
  }

  /** True when all LOA counts are present.
   * Shared by layout class and LOA *ngIf to keep conditions in sync. */
  get isLoaSectionVisible(): boolean {
    return this.sharedService.checkEntitlements(this.entitlementConst.navUUID.addLinkAccess.uuid, '', '')
      && this.accessWithAuthorizedAgentDBS
      && this.addlinkData.loaPendingCount != null
      && this.addlinkData.loaErrorCount != null
      && this.addlinkData.loaInReviewCount != null;
  }
}
